import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertLevel, Button, Text } from '@vizit/atoms';
import { Form, FormTextField } from '@vizit/forms';
import { CheckCircleIcon } from '@vizit/icons';
import { Heading, HeadingVariant, RouterLink } from '@vizit/molecules';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type ForgotPasswordFormSchema = {
  email: string;
};

interface ForgotPasswordFormProps {
  /**
   * Whether the form has been successfully submitted.
   */
  hasSuccessfullySubmitted?: boolean;
  /**
   * Whether the form has a submission error when setting the password.
   */
  hasSubmissionError?: boolean;
  /**
   * Whether the error is due to SSO password reset being disabled.
   */
  hasSsoError?: boolean;
  /**
   * The function to call when the form is submitted.
   */
  onSubmit: (data: ForgotPasswordFormSchema) => Promise<void>;
}

function ForgotPasswordForm(props: ForgotPasswordFormProps) {
  const { hasSuccessfullySubmitted = false, hasSubmissionError = false, hasSsoError = false, onSubmit } = props;
  // Form and validation schema
  const formSchema = yup.object().shape({
    email: yup.string().email('Invalid email format. Please try again.').required('Please enter your email.'),
  });

  const { control, handleSubmit } = useForm<ForgotPasswordFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
  });

  return (
    <div className="flex-grow flex flex-col justify-center items-center" data-testid="forgot-password-form">
      <div className="justify-center flex flex-col items-center gap-14 p-4">
        <Heading variant={HeadingVariant.H6}>Forgot password</Heading>
        {hasSuccessfullySubmitted ? (
          <div
            className="min-h-[238px] text-success-700 flex flex-col items-center justify-start gap-14 animate-fade-in"
            data-testid="forgot-password-success-message"
          >
            <Text className="text-center">We’ve sent you an email with a link to reset your password.</Text>
            <CheckCircleIcon size={150} />
          </div>
        ) : (
          <>
            <div className="min-h-[70px]">
              {hasSsoError ? (
                <Alert
                  className="min-w-[316px] animate-fade-in"
                  level={AlertLevel.Info}
                  data-testid="forgot-password-sso-alert"
                >
                  <Alert.Title>Your organization uses Single Sign-On.</Alert.Title>
                  <Alert.Description>
                    Password reset is not available - please
                    <RouterLink className="text-sm ml-1" to="/sso">
                      sign in with your company account
                    </RouterLink>
                    .
                  </Alert.Description>
                </Alert>
              ) : hasSubmissionError ? (
                <Alert
                  className="min-w-[316px] animate-fade-in"
                  level={AlertLevel.Error}
                  data-testid="forgot-password-error-alert"
                >
                  <Alert.Description>Something went wrong. Please try again.</Alert.Description>
                </Alert>
              ) : (
                <Text
                  className="data-[error]:animate-fade-out max-w-[380px] text-center"
                  data-error={hasSubmissionError || undefined}
                  showEllipsis={false}
                >
                  Let’s get you back in. Enter your email, and we’ll send over a reset link.
                </Text>
              )}
            </div>
            <Form className="gap-4 min-w-[380px]" onSubmit={handleSubmit(onSubmit)}>
              <FormTextField
                isRequired
                label="Email"
                control={control}
                name="email"
                inputProps={{ 'data-testid': 'forgot-password-email-input' }}
              />
              <Button type="submit" data-testid="forgot-password-submit-button">
                Send email
              </Button>
            </Form>
          </>
        )}
        <RouterLink to="/login" data-testid="forgot-password-return-link">
          Return to login
        </RouterLink>
      </div>
    </div>
  );
}

export { ForgotPasswordForm };
export type { ForgotPasswordFormSchema };
